import React, { useMemo } from 'react';
import Card from '../common/Card';
import Icon from '../common/Icon';
import Tooltip from './Tooltip';
import { POSITION_RANGES } from '../../utils/constants';
import './PositionDistribution.css';

const DEFAULT_MAX_DOMAINS = 10;
const DEFAULT_TITLE = "Répartition des positions par domaine";

const PositionDistribution = ({
    data,
    excludedDomains = [],
    maxDomains = DEFAULT_MAX_DOMAINS,
    onViewAll,
    title = DEFAULT_TITLE,
    subtitle,
    tooltipText,
}) => {
    const renderTitle = () => {
        if (!tooltipText) {
            return <h3 className="chart-title">{title}</h3>;
        }
        return (
            <Tooltip text={tooltipText}>
                <div className="position-distribution-title">
                    <h3 className="chart-title">{title}</h3>
                    <Icon name="Info" size={14} className="position-distribution-info-icon" />
                </div>
            </Tooltip>
        );
    };
    const visibleData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.filter(item => !excludedDomains.includes(item.domain));
    }, [data, excludedDomains]);

    const filteredData = useMemo(() => {
        return visibleData.slice(0, maxDomains);
    }, [visibleData, maxDomains]);

    const hasMoreDomains = visibleData.length > filteredData.length;

    if (filteredData.length === 0) {
        return (
            <Card className="position-distribution">
                <div className="position-distribution-title-block">
                    {renderTitle()}
                    {subtitle && <p className="chart-subtitle">{subtitle}</p>}
                </div>
                <p className="chart-empty">Aucune donnée disponible</p>
            </Card>
        );
    }

    const chartData = filteredData.map((item) => {
        const segments = item.segments || {};
        const rank1To5 = segments.rank1To5 || 0;
        const rank6To10 = segments.rank6To10 || 0;
        const rank11To20 = segments.rank11To20 || 0;
        const rank21Plus = segments.rank21Plus || 0;
        const total = item.total || (rank1To5 + rank6To10 + rank11To20 + rank21Plus);

        return {
            domain: item.domain,
            rank1To5,
            rank6To10,
            rank11To20,
            rank21Plus,
            total,
            displayValue: item.displayValue ?? total,
            rank1To5Percent: segments.rank1To5Percent ?? (total > 0 ? (rank1To5 / total) * 100 : 0),
            rank6To10Percent: segments.rank6To10Percent ?? (total > 0 ? (rank6To10 / total) * 100 : 0),
            rank11To20Percent: segments.rank11To20Percent ?? (total > 0 ? (rank11To20 / total) * 100 : 0),
            rank21PlusPercent: segments.rank21PlusPercent ?? (total > 0 ? (rank21Plus / total) * 100 : 0),
        };
    });

    return (
        <Card className="position-distribution">
            <div className="position-distribution-title-block">
                {renderTitle()}
                {subtitle && <p className="chart-subtitle">{subtitle}</p>}
            </div>

            <div className="chart-legend">
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: POSITION_RANGES.TOP_5.color }}></span>
                    <span className="legend-label">1-5</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: POSITION_RANGES.TOP_10.color }}></span>
                    <span className="legend-label">6-10</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: POSITION_RANGES.TOP_20.color }}></span>
                    <span className="legend-label">11-20</span>
                </div>
                <div className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: POSITION_RANGES.BELOW_20.color }}></span>
                    <span className="legend-label">21+</span>
                </div>
            </div>

            <div className="chart-container">
                {chartData.map((item, index) => (
                    <div key={index} className="chart-row">
                        <div className="chart-label">{item.domain}</div>
                        <div className="chart-bar-container">
                            <div className="chart-bar">
                                {item.rank1To5Percent > 0 && (
                                    <div
                                        className="chart-segment"
                                        style={{
                                            width: `${item.rank1To5Percent}%`,
                                            backgroundColor: POSITION_RANGES.TOP_5.color,
                                        }}
                                        title={`1-5: ${item.rank1To5} (${item.rank1To5Percent.toFixed(0)}%)`}
                                    >
                                        <span className="segment-value">{item.rank1To5}</span>
                                    </div>
                                )}
                                {item.rank6To10Percent > 0 && (
                                    <div
                                        className="chart-segment"
                                        style={{
                                            width: `${item.rank6To10Percent}%`,
                                            backgroundColor: POSITION_RANGES.TOP_10.color,
                                        }}
                                        title={`6-10: ${item.rank6To10} (${item.rank6To10Percent.toFixed(0)}%)`}
                                    >
                                        <span className="segment-value">{item.rank6To10}</span>
                                    </div>
                                )}
                                {item.rank11To20Percent > 0 && (
                                    <div
                                        className="chart-segment"
                                        style={{
                                            width: `${item.rank11To20Percent}%`,
                                            backgroundColor: POSITION_RANGES.TOP_20.color,
                                        }}
                                        title={`11-20: ${item.rank11To20} (${item.rank11To20Percent.toFixed(0)}%)`}
                                    >
                                        <span className="segment-value">{item.rank11To20}</span>
                                    </div>
                                )}
                                {item.rank21PlusPercent > 0 && (
                                    <div
                                        className="chart-segment"
                                        style={{
                                            width: `${item.rank21PlusPercent}%`,
                                            backgroundColor: POSITION_RANGES.BELOW_20.color,
                                        }}
                                        title={`21+: ${item.rank21Plus} (${item.rank21PlusPercent.toFixed(0)}%)`}
                                    >
                                        <span className="segment-value">{item.rank21Plus}</span>
                                    </div>
                                )}
                            </div>
                            <div className="chart-total">{item.displayValue}</div>
                        </div>
                    </div>
                ))}
            </div>

            {onViewAll && hasMoreDomains && (
                <div className="position-distribution-footer">
                    <button className="position-view-all-btn" onClick={onViewAll}>
                        Voir tous les concurrents ({visibleData.length})
                        <Icon name="ChevronRight" size={16} />
                    </button>
                </div>
            )}
        </Card>
    );
};

export default PositionDistribution;
